import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Terminal, Lock, User, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      <div className="scanline" />
      
      <div className="absolute inset-0 opacity-5">
        <div className="matrix-rain" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md z-10"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <Terminal className="w-20 h-20 mx-auto mb-4 text-terminal-green glow-text" />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold cyber-font glow-text mb-2">
            CYBERBANK
          </h1>
          <p className="text-terminal-green-dim terminal-font text-sm">
            SECURE TERMINAL ACCESS
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="terminal-card p-8"
        >
          <div className="flex items-center gap-2 mb-6 border-b border-terminal-green pb-3">
            <Lock className="w-5 h-5" />
            <span className="terminal-font text-sm uppercase tracking-wider">
              Authentication Required
            </span>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-4 p-3 border border-red-500 bg-red-500/10 flex items-center gap-2"
            >
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-500 text-sm">{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm mb-2 terminal-font flex items-center gap-2">
                <User className="w-4 h-4" />
                USERNAME
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 terminal-input uppercase tracking-wider"
                placeholder="ENTER USERNAME"
                required
                disabled={loading}
                data-testid="login-username-input"
              />
            </div>

            <div>
              <label className="block text-sm mb-2 terminal-font flex items-center gap-2">
                <Lock className="w-4 h-4" />
                PASSWORD
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 terminal-input uppercase tracking-wider pr-12"
                  placeholder="ENTER PASSWORD"
                  required
                  disabled={loading}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-terminal-green hover:text-terminal-green-dim"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full py-3 terminal-button flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Terminal className="w-5 h-5" />
                  </motion.div>
                  <span>AUTHENTICATING...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>ACCESS TERMINAL</span>
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6 pt-6 border-t border-terminal-green/30 text-center text-xs text-terminal-green-dim terminal-font">
            <p>AUTHORIZED PERSONNEL ONLY</p>
            <p className="mt-1">Security Level: MAXIMUM</p>
          </div>
        </motion.div>

        <div className="mt-6 text-center text-xs text-terminal-green/50 terminal-font">
          <p>© 2025 CYBERBANK TERMINAL SYSTEM</p>
          <p className="mt-1">All access monitored and recorded</p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
